import {
  AddressesByZipCodeRequest,
  AddressesByZipCodeResponse,
  RegisterAdminParams,
  SigninAdminResponse,
  SigninAdminParams,
  FetchAdminRolesResponse,
  ReceiptInfoRequest,
  RestApiResponse,
  RegisterUsersParams,
  FetchUsersResponse,
  FetchUserListCsvResponse,
} from '@mytypes/api';

import { ORIGINS } from '~/constants/api';
import { USER_CATEGORY } from '~/common/constants';
import {
  CreateOriginalFormParams,
  CreateOriginalFormResponse,
  FinalizeSurveyParams,
  UpdateQuestionsContentParams,
  UploadImageRequest,
  UploadImageResponse,
  SummaryJsonDataResponse,
  SummaryJsonDataRequest,
  SummaryExcelDataRequest,
  SummaryExcelDataResponse,
  PreviewSurveyResponse,
  PreviewSurveyRequest,
  GetQuestionsContentRequest,
  GetQuestionsContentResponse,
  GetDraftNgWordsResponse,
  OriginalFormCopyRequest,
  OriginalFormCopyResponse,
  PastSurveyResponse,
  UserInquiryResponse,
  CreateUserInquiryParams,
} from '~/common/types/api/users';

import {
  MiniEnqueteRelationsResponse,
  MiniEnqueteRelationsRequest,
} from '~/common/types/api/admin';
import { createGmoAskAsyncThunk, get, post, put } from './utils';

/**
 * ================
 * admin
 * ================
 */
/**
 * 管理者登録
 */
export const registerAdmin = createGmoAskAsyncThunk<RestApiResponse, RegisterAdminParams>(
  'admin/registerAdmin',
  async ({ name, email, password }) => {
    const response = (await post({
      url: `${ORIGINS.GMOASK_BFF}/administrators`,
      params: {
        name,
        email,
        password,
      },
    })) as RestApiResponse;
    return response;
  }
);

/**
 * 管理者ログイン
 */
export const signinAdmin = createGmoAskAsyncThunk<SigninAdminResponse, SigninAdminParams>(
  'admin/signined',
  async ({ email, password }) => {
    const response = await post({
      url: `${ORIGINS.GMOASK_BFF}/administrators/login`,
      params: {
        email,
        password,
      },
    });
    return response;
  }
);

/**
 * ログイン管理者のロール取得。
 */
export const fetchAdminRoles = createGmoAskAsyncThunk<FetchAdminRolesResponse, undefined>(
  'admin/fetchAdminRoles',
  async () => {
    const response = (await post({
      url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
      params: {
        apiUri: '/administrator_roles',
        parameter: null,
        httpMethod: 'GET',
      },
    })) as FetchAdminRolesResponse;
    return response;
  }
);

/**
 * ================
 * users
 * ================
 */
export const registerUser = createGmoAskAsyncThunk<RestApiResponse, RegisterUsersParams>(
  'user/registerUser',
  async ({
    name,
    email,
    password,
    category,
    individualCategory,
    zip,
    pref,
    address,
    companyName,
    departmentName,
    phone,
  }) => {
    const params = {
      name,
      email,
      password,
      category,
      individualCategory,
      zip,
      pref,
      address,
      phone,
    } as RegisterUsersParams;
    if (Number(category) === USER_CATEGORY.CORPORATE) {
      params.companyName = companyName;
      params.departmentName = departmentName;
      params.individualCategory = null;
    }
    const response = (await post({
      url: `${ORIGINS.GMOASK_BFF}/users`,
      params,
    })) as RestApiResponse;
    return response;
  }
);

/*
 * ユーザのデータ全取得する
 */
export const fetchUsers = createGmoAskAsyncThunk<FetchUsersResponse, undefined>(
  'users/fetchUsers',
  async () => {
    const response = (await post({
      url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
      params: {
        apiUri: '/users',
        httpMethod: 'GET',
      },
    })) as FetchUsersResponse;
    return response;
  }
);

/**
 * ユーザーリストのCSVデータ取得。
 */
export const fetchUserListCsv = createGmoAskAsyncThunk<FetchUserListCsvResponse, undefined>(
  'users/fetchUserListCsv',
  async () => {
    const response = (await post({
      url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
      params: {
        apiUri: '/users/export',
        parameter: null,
        httpMethod: 'GET',
      },
    })) as FetchUserListCsvResponse;
    return response;
  }
);

/**
 * ================
 * Original Form
 * ================
 */

/**
 * Create Original Form Questions Content
 */
export const createOriginalForm = createGmoAskAsyncThunk<
  CreateOriginalFormResponse,
  CreateOriginalFormParams
>('/original_forms', async ({ name }) => {
  const response = (await post({
    url: `${ORIGINS.GMOASK_BFF}/original_forms`,
    params: {
      name,
    },
  })) as CreateOriginalFormResponse;
  return response;
});

/**
 * Update Original Form Questions Content
 */
export const updateQuestionsContent = createGmoAskAsyncThunk<
  RestApiResponse,
  UpdateQuestionsContentParams
>('/original_forms/questions_content', async ({ surveyId, questionsContent }) => {
  const response = (await put({
    url: `${ORIGINS.GMOASK_BFF}/original_forms/questions_content`,
    params: {
      surveyId,
      questionsContent,
    },
  })) as RestApiResponse;
  return response;
});

/**
 * Get Original Form Questions Content
 */

export const getQuestionsContent = createGmoAskAsyncThunk<
  GetQuestionsContentResponse,
  GetQuestionsContentRequest
>('/original_forms/questions_content', async ({ surveyId }) => {
  const response = (await get({
    url: `${ORIGINS.GMOASK_BFF}/original_forms/questions_content`,
    params: {
      surveyId,
    },
  })) as GetQuestionsContentResponse;
  return response;
});

/**
 * Finalize Original Form Survey
 */
export const finalizeOriginalFormSurvey = createGmoAskAsyncThunk<
  RestApiResponse,
  FinalizeSurveyParams
>('/original_forms/finalize', async ({ surveyId }) => {
  const response = (await post({
    url: `${ORIGINS.GMOASK_BFF}/original_forms/finalize`,
    params: {
      surveyId,
    },
  })) as RestApiResponse;
  return response;
});

/**
 * Original Form Image Upload API
 */
export const uploadImage = createGmoAskAsyncThunk<UploadImageResponse, UploadImageRequest>(
  '/images/upload',
  async ({ imageData, format }) => {
    const params = {
      imageData,
      format,
    } as UploadImageRequest;

    const response = (await post({
      url: `${ORIGINS.GMOASK_BFF}/images/upload`,
      params,
    })) as UploadImageResponse;
    return response;
  }
);

/**
 * Original Form Get Summary JSON Data
 */
export const getSummaryJsonData = createGmoAskAsyncThunk<
  SummaryJsonDataResponse,
  SummaryJsonDataRequest
>('/original_forms/summary/json', async ({ surveyId }) => {
  const params = { surveyId } as SummaryJsonDataRequest;

  const response = (await get({
    url: `${ORIGINS.GMOASK_BFF}/original_forms/summary/json`,
    params,
  })) as SummaryJsonDataResponse;
  return response;
});

/**
 * Original Form Get Summary Excel Data
 */
export const getSummaryExcelData = createGmoAskAsyncThunk<
  SummaryExcelDataResponse,
  SummaryExcelDataRequest
>('/original_forms/summary/excel', async ({ surveyId, summaryType }) => {
  const params = { surveyId, summaryType } as SummaryExcelDataRequest;

  const response = (await get({
    url: `${ORIGINS.GMOASK_BFF}/original_forms/summary/excel`,
    params,
  })) as SummaryExcelDataResponse;
  return response;
});

/**
 * Original Form Get Preview Draft
 */
export const getPreviewDraft = createGmoAskAsyncThunk<PreviewSurveyResponse, PreviewSurveyRequest>(
  '/original_forms/preview/draft',
  async ({ surveyId }) => {
    const params = { surveyId } as PreviewSurveyRequest;

    const response = (await get({
      url: `${ORIGINS.GMOASK_BFF}/original_forms/preview/draft`,
      params,
    })) as PreviewSurveyResponse;
    return response;
  }
);

/**
 * Original Form Get Preview Finalize
 */
export const getPreviewFinalize = createGmoAskAsyncThunk<
  PreviewSurveyResponse,
  PreviewSurveyRequest
>('/original_forms/preview/finalized', async ({ surveyId }) => {
  const params = { surveyId } as PreviewSurveyRequest;

  const response = (await get({
    url: `${ORIGINS.GMOASK_BFF}/original_forms/preview/finalized`,
    params,
  })) as PreviewSurveyResponse;
  return response;
});

/**
 * Mini Enquete Relations
 */
export const getMiniEnqueteRelations = createGmoAskAsyncThunk<
  MiniEnqueteRelationsResponse,
  MiniEnqueteRelationsRequest
>('/mini_enquete_relations/id', async ({ surveyId }) => {
  const params = { surveyId } as MiniEnqueteRelationsRequest;
  const response = (await post({
    url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
    params: {
      apiUri: '/mini_enquete_relations/id',
      parameter: JSON.stringify(params),
      httpMethod: 'GET',
    },
  })) as MiniEnqueteRelationsResponse;
  return response;
});

/**
 * Get Draft Ng Words
 */
export const getDraftNgWords = createGmoAskAsyncThunk<GetDraftNgWordsResponse, undefined>(
  '/original_forms/draft_ng_words',
  async () => {
    const response = (await get({
      url: `${ORIGINS.GMOASK_BFF}/original_forms/draft_ng_words`,
      params: {},
    })) as GetDraftNgWordsResponse;
    return response;
  }
);

/**
 * Copy original form
 */
export const copyOriginalForm = createGmoAskAsyncThunk<
  OriginalFormCopyResponse,
  OriginalFormCopyRequest
>('/original_forms/copy', async ({ miniEnqueteId }) => {
  const params = { miniEnqueteId } as OriginalFormCopyRequest;

  const response = (await post({
    url: `${ORIGINS.GMOASK_BFF}/original_forms/copy`,
    params,
  })) as OriginalFormCopyResponse;
  return response;
});

/**
 * Get Past Surveys
 */
export const getPastSurveys = createGmoAskAsyncThunk<PastSurveyResponse, undefined>(
  'surveys/get_mini_enquete',
  async () => {
    const response = (await post({
      url: `${ORIGINS.GMOASK_BFF}/handover/gmoask`,
      params: {
        apiUri: '/surveys/get_mini_enquete',
        parameter: null,
        httpMethod: 'GET',
      },
    })) as PastSurveyResponse;
    return response;
  }
);

/**
 * 郵便番号から住所を取得する ユーザー新登録画面
 */
export const getAddressesByZipCode = createGmoAskAsyncThunk<
  AddressesByZipCodeResponse,
  AddressesByZipCodeRequest
>('user/getAddressesByZipCode', async ({ zipCode }) => {
  const params = { zipCode } as AddressesByZipCodeRequest;

  const response = (await get({
    url: `${ORIGINS.GMOASK_BFF}/addresses`,
    params,
  })) as AddressesByZipCodeResponse;
  return response;
});

/**
 * Receipt Request API
 */
export const postReceiptRequest = createGmoAskAsyncThunk<RestApiResponse, ReceiptInfoRequest>(
  'receipt_request',
  async ({ surveyId, name, zipcode, pref, address }) => {
    const params = { surveyId, name, zipcode, pref, address } as ReceiptInfoRequest;

    const response = (await post({
      url: `${ORIGINS.GMOASK_BFF}/surveys/receipt_request`,
      params,
    })) as RestApiResponse;
    return response;
  }
);

/**
 * Create User Inquiry
 */
export const createUserInquiry = createGmoAskAsyncThunk<
  UserInquiryResponse,
  CreateUserInquiryParams
>('inquiry', async ({ subject, message, lastAccessUri }) => {
  const params = { subject, message, lastAccessUri } as CreateUserInquiryParams;

  const response = (await post({
    url: `${ORIGINS.GMOASK_BFF}/inquiry`,
    params,
  })) as UserInquiryResponse;
  return response;
});
